import { useQuery } from '@tanstack/react-query';
import { transferWindowsControllerListTransferWindowsV1 } from '@/api/generated/transfer-windows/transfer-windows';
import type { TransferWindowsControllerListTransferWindowsV1Params } from '@/api/generated/model';

/**
 * Query keys for transfer windows.
 *
 * Deliberately shallow — there is one read endpoint and nothing writes windows,
 * so there is no invalidation hierarchy to model. `agencyClubsCount` on each
 * row is the one tenant-dependent field, and it is the reason the clubs
 * mutations invalidate this list too.
 */
export const transferWindowKeys = {
  all: ['transfer-windows'] as const,
  lists: () => [...transferWindowKeys.all, 'list'] as const,
  list: (params: TransferWindowsControllerListTransferWindowsV1Params) =>
    [...transferWindowKeys.lists(), params] as const,
};

/**
 * The catalog ships 20 windows across 10 leagues, and the endpoint caps `limit`
 * at 100 — so one request is the whole list, today and for any plausible near
 * future. Paginating a list this size would add a scroll interaction to a
 * sidebar that is meant to be read at a glance.
 *
 * If the seed ever grows past 100 windows this silently truncates, which is why
 * the page asserts on `meta.total` rather than trusting the array length.
 */
const ALL_WINDOWS_LIMIT = 100;

/**
 * Every league's windows, chronologically.
 *
 * The list is global: every agency sees all windows across all leagues, because
 * an agency scouting a market it has not entered still needs that market's
 * dates. Sorting is the endpoint's own default — `startDate` ascending, the one
 * list in the API that defaults to ascending, because a window list is read as
 * "what is open now, then what opens next".
 *
 * Windows are seeded reference data that change at most once a season, hence
 * the long `staleTime`; `status` is still derived from the dates on the server
 * on every read, so a cached row cannot claim to be open after it closes for
 * longer than this window.
 */
export function useTransferWindowsQuery() {
  const params = { limit: ALL_WINDOWS_LIMIT };

  return useQuery({
    queryKey: transferWindowKeys.list(params),
    queryFn: ({ signal }) => transferWindowsControllerListTransferWindowsV1(params, { signal }),
    staleTime: 5 * 60_000,
  });
}
